import { useState, Fragment } from "react";
import PackageRowCard from "@/features/packages/components/PackageRowCard";
import PackageCard from "@/features/packages/components/PackageCard";
import FilterPackages from "@/features/packages/components/FilterPackages";
import { useTranslation } from "react-i18next";
import InfiniteScrollTrigger from "@shared/components/InfiniteScrollTrigger";
import { usePackagesInfiniteQuery } from "./hooks/usePackages";
import { toast } from "sonner";
import ThreeDotLoader from "@shared/components/ThreeDotLoader";
import { useIsMobile } from "@shared/hooks/use-mobile";
import ExportPackages from "@/features/packages/components/ExportPackages";
import PrintSpeedafLabels from "@/features/packages/components/PrintSpeedafLabels";
import HeadLayout from "@shared/layouts/HeadLayout";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { Button } from "@shared/components/ui/button";
import { Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PackageList = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { isAdmin, isManager } = useAuth();
  const [params, setParams] = useState({ limit: 10 });

  const handleFilter = (newFilters) => {
    // Replace the entire filter state with new filters
    // This ensures that clearing filters actually removes all previous filter values
    setParams({
      ...newFilters,
      page: 1,
    });
  };

  const {
    data,
    error,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = usePackagesInfiniteQuery(params);

  if (error) {
    toast.error("Error fetching packages", error.message);
  }

  return (
    <div className="space-y-6">
      <HeadLayout title={t("packages.packages")}>
        <div className="flex items-center gap-2">
          {(isAdmin || isManager) && (
            <Button onClick={() => navigate("/packages/create")} size="sm">
              <Plus className="h-4 w-4 mr-1" />
              {t("packages.createPackage")}
            </Button>
          )}
          <PrintSpeedafLabels filter={params} />
          <ExportPackages filter={params} />
        </div>
      </HeadLayout>

      {/* Filter Section */}
      <FilterPackages onFilter={handleFilter} currentFilters={params} />

      {isLoading && !isFetchingNextPage ? (
        <div className="flex items-center justify-center">
          <ThreeDotLoader size="md" />
        </div>
      ) : null}
      {data?.pages[0]?.data?.length === 0 && (
        <div className="flex items-center justify-center">
          <p>{t("packages.noPackagesFound")}</p>
        </div>
      )}
      <div className="flex flex-col gap-4">
        {data?.pages?.map((page) => (
          <Fragment key={page.pageInfo.currentPage}>
            {page?.data.map((pkg) =>
              isMobile ? (
                <PackageCard key={pkg._id} pkg={pkg} />
              ) : (
                <PackageRowCard key={pkg._id} pkg={pkg} />
              )
            )}
          </Fragment>
        ))}
      </div>

      <InfiniteScrollTrigger
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
      />
    </div>
  );
};

export default PackageList;
